import { useMemo, useState } from "react";
import { useHolidays, useManagerAttendance, useManagerCompOffLedger } from "@/api/queries";
import { PageHeader } from "@/app/page-header";
import { AttendanceHeatmap, StatTile, TrendChart } from "@/components/charts";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { AttendanceStatusBadge } from "@/pages/hr/attendance";
import { AttendanceLogDialog } from "@/pages/shared/attendance-log-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { attendanceRate, leaveDayCount, tallyByDay } from "@/lib/analytics";
import { MonthStepper } from "@/pages/shared/month-stepper";
import {
  classifyDay,
  currentMonth,
  eachDateInRange,
  holidayMap,
  leaveMap,
  monthBounds,
  todayStr,
  weekBounds,
  wfhMap,
} from "@/lib/calendar";
import { formatDate, formatDays, formatTime } from "@/lib/format";
import { formatMinutes } from "@/lib/org-time";
import { WORK_MODE_LABEL } from "@/lib/labels";

type RangeKey = "today" | "week" | "month";

function rangeFor(key: RangeKey, month: string) {
  const today = todayStr();
  if (key === "today") return { start: today, end: today };
  if (key === "week") return weekBounds(today);
  return monthBounds(month);
}

export function ManagerAttendancePage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>("week");
  // Which month the Month tab shows. Starts on the current one; the arrows step
  // back through earlier months.
  const [month, setMonth] = useState(currentMonth());
  const [openPerson, setOpenPerson] = useState<{ id: number; name: string } | null>(null);

  const range = useMemo(() => rangeFor(rangeKey, month), [rangeKey, month]);
  const attendance = useManagerAttendance(range.start, range.end);
  // The Today table is always about today. When the range being browsed is an
  // earlier month it holds nothing for today, so today is fetched on its own.
  const today = todayStr();
  const rangeHasToday = range.start <= today && today <= range.end;
  const todayOnly = useManagerAttendance(today, today, !rangeHasToday);
  const todaySource = rangeHasToday ? attendance : todayOnly;
  const holidays = useHolidays();
  const compOff = useManagerCompOffLedger();

  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);
  const people = attendance.data?.users ?? [];
  const records = attendance.data?.records ?? [];
  // Team leave for the same range, indexed by person-day — see lib/calendar.ts.
  const leave = useMemo(() => leaveMap(attendance.data?.leave), [attendance.data?.leave]);
  // Work from home, indexed the same way. Kept separate from `leave` throughout:
  // a WFH day is a day worked, so it must never reach the absence arithmetic.
  const wfh = useMemo(() => wfhMap(attendance.data?.wfh), [attendance.data?.wfh]);

  const dates = useMemo(() => eachDateInRange(range.start, range.end), [range.start, range.end]);
  const dayTallies = useMemo(
    () => tallyByDay(people, records, range.start, range.end, holidayNames, leave),
    [people, records, range.start, range.end, holidayNames, leave],
  );
  const rate = attendanceRate(dayTallies);
  const leaveDays = leaveDayCount(dayTallies);

  const todayRecords = todaySource.data?.records;
  const todayByUser = useMemo(() => {
    const map = new Map<number, (typeof records)[number]>();
    for (const record of todayRecords ?? []) if (record.date === today) map.set(record.user_id, record);
    return map;
  }, [todayRecords, today]);
  const todayLeave = useMemo(() => leaveMap(todaySource.data?.leave), [todaySource.data?.leave]);
  const todayWfh = useMemo(() => wfhMap(todaySource.data?.wfh), [todaySource.data?.wfh]);

  const compOffEarned = (compOff.data ?? [])
    .filter((entry) => entry.type === "Earned")
    .reduce((sum, entry) => sum + entry.amount, 0);

  const trendData = dayTallies.map((tally) => ({
    day: formatDate(tally.date, "d MMM") ?? tally.date,
    complete: tally.complete,
    half: tally.half,
    leave: tally.leave,
    absent: tally.absent,
  }));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Scoped to your direct reports"
        title="Team attendance"
        sub="Check-in time, check-out time and full history for every direct report. You can correct a day for anyone on your own team."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={rangeKey} onValueChange={(value) => setRangeKey(value as RangeKey)}>
          <TabsList>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="week">This week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
        {rangeKey === "month" && <MonthStepper month={month} onChange={setMonth} />}
        <span className="font-mono text-xs text-muted">
          {formatDate(range.start)} to {formatDate(range.end)}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Direct reports" value={people.length} icon="team" loading={attendance.isLoading} />
        <StatTile
          label="Full days in range"
          value={rate === null ? "—" : `${rate}%`}
          icon="check-circle"
          loading={attendance.isLoading}
          meta={
            rate === null ? "Nothing recorded in this range" : "of working-day slots, leave excluded"
          }
        />
        <StatTile
          label="On leave in range"
          value={leaveDays}
          icon="leave"
          loading={attendance.isLoading}
          meta="Approved leave days across your team"
        />
        <StatTile
          label="Comp off earned"
          value={formatDays(compOffEarned)}
          icon="calendar-check"
          loading={compOff.isLoading}
          meta="Days credited to your team"
        />
      </div>

      <TrendChart
        title="Attendance across the range"
        note="Working days only. Approved leave is counted as leave, not as absence."
        data={trendData}
        xKey="day"
        series={[
          { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
          { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
          { key: "leave", label: "On leave", color: STATUS_SERIES_COLOR.leave },
          { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
        ]}
        loading={attendance.isLoading}
        {...(attendance.error ? { error: attendance.error.message } : {})}
        emptyBody="No working days with recorded attendance in this range."
      />

      {rangeKey !== "today" && (
        <AttendanceHeatmap
          title="Every report, every day"
          note="One cell per person per day across the selected range"
          people={people.map((person) => ({ id: person.id, name: person.name }))}
          records={records}
          dates={dates}
          holidays={holidayNames}
          leave={leave}
          wfh={wfh}
          loading={attendance.isLoading}
          emptyBody="No direct reports mapped to your team yet."
        />
      )}

      <Panel>
        <PanelHeader title="Today" note={`${formatDate(today)} · open a row for the full history`} />
        {todaySource.isLoading ? (
          <SkeletonTable rows={5} cols={5} />
        ) : (todaySource.data?.users ?? []).length === 0 ? (
          <EmptyState
            icon="team"
            title="No direct reports yet"
            body="Employees appear here once HR maps their account to your team."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Mode</TH>
              <TH>Check in</TH>
              <TH>Check out</TH>
              <TH numeric>Hours</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">History</span>
              </TH>
            </THead>
            <TBody>
              {(todaySource.data?.users ?? []).map((person) => {
                const record = todayByUser.get(person.id);
                const onLeave = todayLeave.get(`${person.id}|${today}`);
                const fromHome = todayWfh.get(`${person.id}|${today}`);
                // The day's TOTAL across its sessions. Not last-out minus
                // first-in, which spans the breaks once a day has more than one
                // session, and not mode-dependent — every mode is judged on hours.
                const minutes = record?.total_minutes ?? 0;
                return (
                  <TR key={person.id}>
                    <TDPrimary>{person.name ?? "Unnamed"}</TDPrimary>
                    <TD>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {record?.mode
                          ? WORK_MODE_LABEL[record.mode]
                          : person.work_mode
                            ? WORK_MODE_LABEL[person.work_mode]
                            : "—"}
                        {/* Approved work from home today. Shown whether or not
                            they have clocked in yet: before they do, it is the
                            only thing on the row that explains an empty
                            check-in; after, it says the remote day was agreed
                            rather than taken. */}
                        {fromHome?.status === "approved" && (
                          <Badge tone="accent" icon="wfh">
                            WFH
                          </Badge>
                        )}
                      </span>
                    </TD>
                    <TD className="font-mono text-xs">{formatTime(record?.check_in ?? null) ?? "—"}</TD>
                    <TD className="font-mono text-xs">{formatTime(record?.check_out ?? null) ?? "—"}</TD>
                    <TD numeric className="font-mono text-xs">
                      {minutes > 0 ? formatMinutes(minutes) : "—"}
                    </TD>
                    <TD>
                      <AttendanceStatusBadge
                        kind={classifyDay(today, record, holidayNames, onLeave, fromHome)}
                        leave={onLeave}
                      />
                    </TD>
                    <TD>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpenPerson({ id: person.id, name: person.name ?? "Unnamed" })}
                      >
                        History
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <Panel>
        <PanelHeader title="Comp off ledger" note="Your team only" />
        {compOff.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : (compOff.data ?? []).length === 0 ? (
          <EmptyState
            icon="calendar-check"
            title="No comp off earned yet"
            body="Working a weekly off or company holiday credits comp off automatically."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Date</TH>
              <TH>Entry</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
            </THead>
            <TBody>
              {(compOff.data ?? []).slice(0, 20).map((entry, index) => (
                <TR key={`${entry.employee_name}-${entry.date}-${index}`}>
                  <TDPrimary>{entry.employee_name ?? "Unnamed"}</TDPrimary>
                  <TD className="font-mono text-xs">{formatDate(entry.date)}</TD>
                  <TD>
                    <Badge tone={entry.type === "Earned" ? "good" : "neutral"}>{entry.type}</Badge>
                  </TD>
                  <TD numeric>{entry.amount}</TD>
                  <TD className="max-w-[28ch] truncate">{entry.reason}</TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      {openPerson && (
        <AttendanceLogDialog
          scope="manager"
          person={openPerson}
          onClose={() => setOpenPerson(null)}
          holidays={holidayNames}
        />
      )}
    </div>
  );
}
